import React, { useContext, useState } from "react";
import { ASSETS_URL, store } from "../../../../components/Store";
import { Icon } from "@iconify/react";
import moment from "moment";
import "moment/locale/es";
import { TimeZones } from "./time-zones";
import { Share } from "./share";
import { ScheduleEvent } from "./schedule-event";
import { useTranslationsById } from "../../../../hooks/use-translations-by-id";
import { CommunitySignInRequestModal } from "../../../../components/community/community-sign-in-request-modal";
import { useTimeZonesDates } from "../../../../hooks/use-timezones-dates";

/**
 * Tarjeta principal de un evento de MakeMake Live.
 *
 * Expone información, accesos y capas secundarias de interacción del evento.
 */
export const EventCard = ({ eventData, lang }) => {
	const colombianTimezone = "America/Bogota";
	const date = eventData?.fechasZonas?.co || `${eventData?.fecha} ${eventData?.hora}`;
	const timezonesDates = useTimeZonesDates({
		fullDate: date,
		timeZoneSource: colombianTimezone,
	});

	const globalState = useContext(store);
	const isAuth = globalState?.state?.sesionActiva === "SI";
	const texts = useTranslationsById({ mainSectionName: "reading_resources", language: lang });
	const [showTimeZoneInfo, setShowTimeZoneInfo] = useState(false);
	const [showShare, setShowShare] = useState(false);
	const [showScheduleEvent, setShowScheduleEvent] = useState(false);
	const eventType = eventData?.tipoEvento?.toLowerCase();
	const isPrivateEvent = eventType?.includes("exclusivo");
	const [showSignInModal, setShowSignInModal] = useState(false);
	const title =
		lang === "en"
			? eventData?.titulo?.en || eventData?.titulo?.es || "N/A"
			: eventData?.titulo?.es || "N/A";
	const description =
		lang === "en"
			? eventData?.descripcion?.en || eventData?.descripcion?.es || "N/A"
			: eventData?.descripcion?.es || "N/A";
	const guests = Array.isArray(eventData?.invitados) ? eventData?.invitados?.join(",") : "N/A";

	const getMainDataByLang = ({ lang }) => {
		const eventDate = eventData?.fechasZonas?.co;
		if (lang === "es") {
			moment.locale("es");
		} else {
			moment.locale("en");
		}
		return {
			month:
				moment(eventDate).format("MMMM[,] YYYY").charAt(0).toUpperCase() +
				moment(eventDate).format("MMMM[,] YYYY").slice(1).toLowerCase(),
			day: moment(eventDate).format("DD"),
			dayName:
				moment(eventDate).format("dddd").charAt(0).toUpperCase() +
				moment(eventDate).format("dddd").slice(1).toLowerCase(),
			hour: moment(eventDate).format("h:mm A"),
		};
	};

	const mainDateData = getMainDataByLang({ lang });
	const imageUrl = `${ASSETS_URL}${eventData?.imagenUrl}`;
	const eventAvailable =
		eventData?.estadoBoton && eventData?.estadoBoton?.toLowerCase()?.includes("ver");

	const getTimezones = () => {
		return [
			`${texts.getText({
				id: "makemake_en_vivo.evento.timezones.colombia",
				defaultText: "Colombia",
			})} - ${timezonesDates?.hours?.colombia}`,
			`${texts.getText({
				id: "makemake_en_vivo.evento.timezones.mexico",
				defaultText: "Mexico",
			})} - ${timezonesDates?.hours?.mexico}`,
			`${texts.getText({
				id: "makemake_en_vivo.evento.timezones.argentina",
				defaultText: "Argentina",
			})} - ${timezonesDates?.hours?.argentina}`,
			`${texts.getText({
				id: "makemake_en_vivo.evento.timezones.chile",
				defaultText: "Chile",
			})} - ${timezonesDates?.hours?.chile}`,
			`${texts.getText({
				id:
					timezonesDates?.timezonesInfo?.usa?.toLowerCase() === "cdt"
						? "makemake_en_vivo.evento.timezones.usa.cdt"
						: "makemake_en_vivo.evento.timezones.usa.cst",
				defaultText:
					timezonesDates?.timezonesInfo?.usa?.toLowerCase() === "cdt"
						? "US (Central Daylight Time)"
						: "US (Central Standard Time)",
			})} - ${timezonesDates?.hours?.usa}`,
		];
	};

	const handleLinkAccess = (link) => {
		if (isAuth === false && isPrivateEvent) {
			setShowSignInModal(true);
			return;
		}
		window.open(link, "_blank");
	};

	return (
		<>
			<article className="row w-100 p-0 m-0 event-card my-3" style={{ maxWidth:800, minHeight: 300 }}>
				<div className="SECTION-1 col-12 col-md-8 p-0">
					<div style={{ position: "relative" }}>
						<img
							className="img d-none d-md-flex"
							src={imageUrl}
							alt={`${title}-img`}
							loading="lazy"
							width={"100%"}
							height={100}
						/>
						<p className="px-2 py-1 text-12 event-type secondary-font">
							{isPrivateEvent
								? texts.getText({
										id: "makemake_en_vivo.evento.tipo_evento.privado",
										defaultText: "Exclusive event",
									})
								: texts.getText({
										id: "makemake_en_vivo.evento.tipo_evento.publico",
										defaultText: "Public event",
									})}
						</p>
					</div>
					<div className="px-4 py-3">
						<h3 className="title subheading-1  my-2 mt-4 mt-md-2">{title}</h3>
						<h4 className="guests subheading-2">
							{texts.getText({
								id: "makemake_en_vivo.evento.invitados",
								defaultText: "Guest(s)",
							})}
							: {guests}
						</h4>
						<p className="description my-2 body-base">{description}</p>
					</div>
				</div>
				<div className="SECTION-2 col-12 col-md py-3 date-info-container d-flex flex-column align-items-center justify-content-center overflow-hidden">
					<div className="d-flex flex-column align-items-center justify-content-center">
						<p className="secondary-font font-weight-bold">{mainDateData.dayName}</p>
						<p
							className="text-50 font-weight-bolder secondary-font"
							style={{ lineHeight: "1em" }}
						>
							{mainDateData.day}
						</p>
						<p className="secondary-font font-weight-bold">{mainDateData.month}</p>
						<p
							className="text-30 font-weight-bolder secondary-font mt-2"
							style={{ lineHeight: "1em" }}
						>
							{mainDateData.hour} <span>(CT)</span>
						</p>
						<button
							className="focusable-red d-flex flex-column"
							onClick={() => setShowTimeZoneInfo(true)}
						>
							<p className="p-0 d-flex align-items-center font-weight-bold secondary-font onbackground-color">
								{texts.getText({
									id: "makemake_en_vivo.evento.boton.horarios",
									defaultText: "Schedules",
								})}
								<Icon icon="iconamoon:arrow-right-2-bold" width={22} />
							</p>
						</button>
					</div>
					<div className="my-2 d-flex flex-column">
						<>
							{eventAvailable ? (
								<button
									className="focusable-red watch-button d-flex px-4 py-2 my-1 justify-content-center align-items-center text-center"
									onClick={() => handleLinkAccess(eventData?.link)}
								>
									<span>
										{texts.getText({
											id: "makemake_en_vivo.evento.boton.ver",
											defaultText: "Watch",
										})}
									</span>
								</button>
							) : null}

							{eventAvailable === false ? (
								<button
									className="focusable-red light-button d-flex px-4 py-2 my-1 justify-content-center align-items-center text-center"
									onClick={() => handleLinkAccess(eventData?.link)}
								>
									<span>
										{texts.getText({
											id: "makemake_en_vivo.evento.boton.inscribirse",
											defaultText: "Register",
										})}
									</span>
								</button>
							) : null}

							<button
								className="focusable-red light-button d-flex px-4 py-2 my-1 justify-content-center align-items-center text-center"
								onClick={() => setShowShare(true)}
							>
								<span>
									{texts.getText({
										id: "makemake_en_vivo.evento.boton.compartir",
										defaultText: "Share",
									})}
								</span>{" "}
								<Icon className="pl-2" icon="gridicons:share" width={30} />
							</button>
						</>
						{eventAvailable === false ? (
							<button
								className="focusable-red light-button d-flex px-4 py-2 my-1 justify-content-center align-items-center text-center"
								onClick={() => setShowScheduleEvent(true)}
							>
								<span>
									{texts.getText({
										id: "makemake_en_vivo.evento.boton.agendar",
										defaultText: "Schedule",
									})}
								</span>{" "}
								<Icon icon="mingcute:schedule-fill" className="pl-2" width={30} />
							</button>
						) : null}
						<TimeZones
							list={getTimezones()}
							visible={showTimeZoneInfo}
							setVisible={setShowTimeZoneInfo}
							lang={lang}
						/>
						<Share
							link={eventData?.link || ""}
							description={
								eventAvailable
									? texts.getText({
											id: "makemake_en_vivo.evento.compartir.link_evento",
											defaultText: "Copy and share the event link.",
										})
									: texts.getText({
											id: "makemake_en_vivo.evento.compartir.inscripcion",
											defaultText:
												"Copy and share the event registration form link.",
										})
							}
							visible={showShare}
							setVisible={setShowShare}
						/>
						<ScheduleEvent
							eventData={eventData}
							visible={showScheduleEvent}
							setVisible={setShowScheduleEvent}
							lang={lang}
						/>
					</div>
				</div>
			</article>
			<CommunitySignInRequestModal
				visible={showSignInModal}
				setVisible={setShowSignInModal}
				title={texts.getText({
					id: "popups.iniciar_sesion.titulo",
					defaultText: "Exclusive event!",
				})}
				description={texts.getText({
					id: "popups.iniciar_sesion.descripcion",
					defaultText: "Sign in to access this content.",
				})}
				buttonText={texts.getText({
					id: "popups.iniciar_sesion.boton",
					defaultText: "Sign in",
				})}
				redirectionParam={"/comunidad?section=makemake-en-vivo"}
			/>
		</>
	);
};
